import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { useRef, useState, useEffect, useMemo } from 'react';
import { getImageUrl } from '@/utils/images';

const LG_SIZE = 1024;
const MD_SIZE = 768;

type CarouselItems = {
    image?: {
        url: string;
        alt: string;
        width: number;
        height: number;
    };
    link?: {
        url: string;
        rel?: string;
        title: string;
        target?: string;
    };
    title?: string;
    description?: string;
};

export type CarouselProps = {
    slides: CarouselItems[];
    headingLevel?: string;
    numSlidesTablet?: number;
    numSlidesDesktop?: number;
    useZoomAnimation?: boolean;
};

export const Carousel = ({ slides = [], headingLevel = '3', numSlidesTablet = 3, numSlidesDesktop = 4, useZoomAnimation = false }: CarouselProps) => {
    const desktopRef = useRef<HTMLDivElement | null>(null);
    const defaultRef = useRef<HTMLDivElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);

    const [index, setIndex] = useState(0);
    const [active, setActive] = useState(-1);
    const [canGoLeft, setCanGoLeft] = useState(false);
    const [canGoRight, setCanGoRight] = useState(false);

    const numSlides = useMemo(() => slides.length, [slides]);

    // These are only used for the desktop version. The ohter versions are easier to calculate with CSS alone
    const showItems = Math.min(numSlidesDesktop, numSlides);
    const showItemsTablet = Math.min(numSlidesTablet, numSlides);

    let bigSize = 30;
    if (numSlides == 4) {
        bigSize = 40;
    } else if (showItems <= 2) {
        bigSize = 65;
    } else if (showItems <= 3) {
        bigSize = 53;
    }

    const delta = numSlides - 1;
    const inactiveSize = 100 / numSlides;

    const totalOutside = (numSlides - showItems) * inactiveSize;
    const smallSize = (100 - totalOutside - bigSize) / delta;
    const totalSize = (100 / showItems) * numSlides;

    function getItemClasses(largeWithZoom = false) {
        const classes: string[] = [];

        if (numSlidesTablet > 2 && numSlides > 2) {
            classes.push('md:r-min-w-[calc(33%-0.75rem)]');
        } else {
            classes.push('md:r-min-w-[calc(50%-0.75rem)]');
        }

        if (largeWithZoom) {
            return classes;
        }

        if (numSlidesDesktop > 3 && numSlides > 3) {
            classes.push('xl:r-min-w-[calc(25%-1.25rem)]');
        } else {
            classes.push('xl:r-min-w-[calc(33%-0.75rem)]');
        }

        return classes;
    }

    function getHeaderTag() {
        switch (headingLevel) {
            case '2':
                return 'h2';
            case '3':
                return 'h3';
            case '4':
                return 'h4';
            case '5':
                return 'h5';
            case '6':
                return 'h6';
        }

        return 'h3';
    }

    const getItems = (largeWithZoom = false) =>
        slides.map((item, itemIndex) => {
            const inViewport = itemIndex >= index && itemIndex < index + showItems;
            const style: React.CSSProperties = {};
            const buffer = 24;

            if (largeWithZoom) {
                style.minWidth = `calc(${inactiveSize}% - ${buffer}px)`;
                style.transition = 'all ease-in-out 400ms';
                if (!inViewport) {
                    style.minWidth = `calc(${inactiveSize}% - ${buffer}px)`;
                    style.maxWidth = `calc(${inactiveSize}% - ${buffer}px)`;
                    delete style.transition;
                } else {
                    if (itemIndex === active) {
                        style.minWidth = `calc(${bigSize}% - ${0}px)`;
                    } else if (active !== -1) {
                        style.minWidth = `calc(${smallSize}% - ${buffer}px)`;
                    }
                }
            }

            const HeaderTag = getHeaderTag();

            return (
                <Link
                    style={style}
                    to={item.link?.url || '/'}
                    rel={item.link?.rel || ''}
                    onMouseOut={() => setActive(-1)}
                    key={item.title || '' + itemIndex}
                    target={item.link?.target || '_self'}
                    onMouseOver={() => setActive(itemIndex)}
                    className={clsx(
                        'r-group r-flex r-min-h-[475px] r-flex-1 r-snap-start r-flex-col r-overflow-hidden r-bg-white r-duration-300 max-md:r-min-w-[80vw] max-md:r-pl-4 lg:r-w-full',
                        getItemClasses(largeWithZoom)
                    )}
                >
                    <div
                        role="img"
                        aria-label={item.image?.alt || ''}
                        style={{ backgroundImage: `url(${getImageUrl(item.image?.url)})` }}
                        className="r-max-h-[350px] r-min-h-[350px] r-w-full r-flex-1 r-rounded-xl r-bg-cover r-bg-center r-p-2"
                    />
                    <div className="r-pt-4">
                        <HeaderTag className="r-pb-1 r-text-lg r-font-bold">{item.title}</HeaderTag>
                        <div
                            className={clsx({
                                'r-pb-3': !useZoomAnimation,
                                'r-overflow-hidden r-transition-all r-duration-300 group-hover:r-pb-3 lg:r-max-h-0 lg:group-hover:r-max-h-full':
                                    useZoomAnimation,
                            })}
                        >
                            <p className="r-text-sm">{item.description}</p>
                        </div>
                        <p className="r-text-black">{item.link?.title}</p>
                    </div>
                </Link>
            );
        });

    function getCurrentRef() {
        if (useZoomAnimation) {
            if (!desktopRef?.current || !containerRef?.current || !defaultRef?.current) {
                return null;
            }

            return containerRef!.current!.offsetWidth >= LG_SIZE ? desktopRef!.current! : defaultRef!.current!;
        }

        return defaultRef!.current || null;
    }

    function getScrollAmount(ref: HTMLElement) {
        if (ref.clientWidth < MD_SIZE) {
            return ref.clientWidth;
        }

        const numItems = ref.clientWidth >= LG_SIZE ? showItems : showItemsTablet;
        return ref.clientWidth / numItems;
    }

    const slide = (direction: number) => {
        if ((direction > 0 && !canGoRight) || (direction < 0 && !canGoLeft)) {
            return;
        }

        const ref = getCurrentRef();
        if (!ref) {
            return;
        }

        const scrollAmount = getScrollAmount(ref);
        ref.scrollBy({ left: direction * scrollAmount + 16 * direction + 1 * direction, behavior: 'smooth' });
    };

    const handleScroll = () => {
        const ref = getCurrentRef();
        if (!ref) {
            return;
        }

        const left = ref.scrollLeft > 0;
        const right = ref.scrollLeft < ref.scrollWidth - containerRef!.current!.offsetWidth - 8;
        const itemWidth = containerRef!.current!.offsetWidth / showItems;

        setCanGoLeft(left);
        setCanGoRight(right);

        // set the correct activet item so we can calculate later whats offscreen
        // We do do this on scroll so that even when someone scrolls, we know where we are
        setIndex(Math.floor(ref.scrollLeft / itemWidth));
    };

    useEffect(() => {
        desktopRef.current?.addEventListener('scroll', handleScroll);
        defaultRef.current?.addEventListener('scroll', handleScroll);
        handleScroll();

        return () => {
            desktopRef.current?.removeEventListener('scroll', handleScroll);
            defaultRef.current?.removeEventListener('scroll', handleScroll);
        };
    }, [desktopRef, containerRef]);

    const leftButtonClasses = clsx(
        'max-md:r-hidden r-absolute r-left-4 lg:-r-left-6 r-top-1/2 r--translate-y-[80px] r-rounded-full r-bg-white r-shadow r-z-10 r-p-2 r-translate-x-7 lg:r-translate-x-10 r-transition-opacity',
        {
            'r-opacity-0': !canGoLeft,
        }
    );

    const rightButtonClasses = clsx(
        'max-md:r-hidden r-absolute r-right-4 lg:-r-right-6 r-top-1/2 r--translate-y-[80px] r-rounded-full r-bg-white r-shadow r-z-10 r-p-2 r--translate-x-7 lg:r--translate-x-10 r-transition-opacity',
        {
            'r-opacity-0': !canGoRight,
        }
    );

    return (
        <div
            ref={containerRef}
            className={clsx('r-flex', {
                'md:r-overflow-hidden': !useZoomAnimation,
            })}
        >
            <button onClick={() => slide(-1)} className={leftButtonClasses}>
                <svg className="r-h-6 r-w-6 r-pr-[2px]" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M15.7071 4.29289C16.0976 4.68342 16.0976 5.31658 15.7071 5.70711L9.41421 12L15.7071 18.2929C16.0976 18.6834 16.0976 19.3166 15.7071 19.7071C15.3166 20.0976 14.6834 20.0976 14.2929 19.7071L7.29289 12.7071C7.10536 12.5196 7 12.2652 7 12C7 11.7348 7.10536 11.4804 7.29289 11.2929L14.2929 4.29289C14.6834 3.90237 15.3166 3.90237 15.7071 4.29289Z"
                        fill="#000000"
                    />
                </svg>
            </button>

            <button onClick={() => slide(1)} className={rightButtonClasses}>
                <svg className="r-h-6 r-w-6 r-pl-[2px]" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M8.29289 4.29289C8.68342 3.90237 9.31658 3.90237 9.70711 4.29289L16.7071 11.2929C17.0976 11.6834 17.0976 12.3166 16.7071 12.7071L9.70711 19.7071C9.31658 20.0976 8.68342 20.0976 8.29289 19.7071C7.90237 19.3166 7.90237 18.6834 8.29289 18.2929L14.5858 12L8.29289 5.70711C7.90237 5.31658 7.90237 4.68342 8.29289 4.29289Z"
                        fill="#000000"
                    />
                </svg>
            </button>

            <div
                ref={defaultRef}
                className={clsx(
                    'scrollbar-hide r-flex r-w-full r-snap-x r-snap-mandatory r-overflow-x-auto r-transition-all r-duration-300 max-md:-r-mx-4 max-md:r-min-w-[100vw] max-md:r-max-w-[100vw] max-md:r-pr-6 max-md:last:r-pr-4 md:r-gap-6',
                    {
                        'lg:r-hidden': useZoomAnimation,
                    }
                )}
            >
                {getItems()}
            </div>

            {useZoomAnimation && (
                <div className="scrollbar-hide r-w-full r-snap-x r-snap-mandatory r-overflow-x-scroll max-lg:r-hidden" ref={desktopRef}>
                    <div
                        style={{ width: `calc(${totalSize}%)` }}
                        className="r-hidden r-snap-x r-snap-start r-gap-6 r-scroll-smooth r-transition-all r-duration-300 lg:r-flex"
                    >
                        {getItems(true)}
                    </div>
                </div>
            )}
        </div>
    );
};
